//Hooks
import { get } from 'svelte/store';
//Helpers
import { getExtSensorsStatus } from '../../helpers/ext_sensor_helpers';
import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
import {
	real_id,
	is_gen_2,
	tmp_units_from_settings,
	celsius_to_fahrenheit,
	isThreeEM,
	isEM,
	isWallDisplay,
	isWeatherStation,
	isPlusUni,
	isPill,
	isFanDevice,
} from '../../helpers/interface_helpers';
import { isPillTemperatureMeasurement } from 'src/helpers/thepill/thepill_data_helpers';
import { getTariffByID } from '../../helpers/power_prices_helpers';
import { average, convertCost, mathEpsilon, sortOn, sum } from '../../helpers/utilities';
import {
	addDate,
	dateFormat,
	getNumberOfWeekDaysInMonth,
	getUTCOffset,
	startsWeekOnSun,
} from '../../helpers/date_helper';
import { devices_config } from '@devices/index';
import { isBleStatisticsDeviceToUTC, isPrevPeriodComparisonEnabled } from './chart_control_helpers';
import type { chartControlsDataInterface, rangeOptionInterface } from './chart_control_helpers';
import {
	getComponentByDevice,
	isVirtualDevice,
} from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { bthomeObjectInfos } from 'src/helpers/virtual_helpers/virtual_bthome_helper';
import { getPeripheralDeviceStatus, isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
//Stores
import { user_settings } from 'src/stores/account/user';
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import {
	getSystemStyle,
	CHART_VARIANTS_LIST,
	checkForStatisticError,
	deepCloneObj,
} from './chart_helper';
import { SystemStyles } from '@Shelly/shelly-design-system';
import { convertNeoValveChartUnits } from 'src/helpers/xt1_helpers/template_units_helpers';
import { convertToUnit, convertToValueAndUnit, skipUnitConversion } from 'src/helpers/units_helpers';
import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
import { TEMPERATURE_SENSORS_IN_CELSIUS } from 'src/constants';
import { calculateNetMetering } from './chart_net_metering_helpers';
import { isPillPeripheralDevice } from 'src/helpers/thepill/thepill_data_helpers';
import { convertPrecipitationFromMm } from 'src/helpers/units/precipitation_unit';
//Types
import type { Device } from '../../types/Device';
import type {
	ChartConfig,
	ChartConfigInterface,
	ChartStatisticInterface,
	ChartDataInterface,
	ChartVariantsType,
	ConsumptionDataset,
} from '../../types/Chart';
import type {
	GetStatsResponseData,
	GetStatsResponseHistoryData,
	MonthlyReport,
} from '../../types/requests/charts/get_stats_period';
import type { IChartTempSchedule } from '../../types/TemperatureSchemeType';
import type { PowerPricesData } from '../../types/PowerPrices';

export const DEFAULT_CHART_HEIGHT = 260;
const TEMP_SCHEME_CHART_HEIGHT = 200;

export const getChartData = (
	data: GetStatsResponseData,
	chartConfig: ChartConfig,
	rangeValue: string,
	device: Device = null,
	prevPeriodData = null,
	dummyNetMetering: boolean = false,
	period: rangeOptionInterface['period'] | null = null,
): ChartDataInterface => {
	if (isXT1Module(chartConfig?.deviceType)) {
		convertNeoValveChartUnits(data, chartConfig);
	}
	const chartData: ChartDataInterface = {
		...chartConfig,
		rangeValue,
		rangeInterval: data.history_interval || data.interval,
		series: [],
	};
	const series = chartConfig.series || [],
		threeEM = isThreeEM(device?.type) && device?.mode != 'monophase',
		currency = get(user_settings).currency || '';
	let totalConsumption = 0,
		totalConsumptionCost = 0,
		totalReversedConsumption = 0,
		missingData = true;

	const settings = devices_settings.getByRealId(device?.id);

	const isPillTempMeasurement = isPillTemperatureMeasurement(
		chartConfig,
		chartData.units,
		device?.type,
	);
	const isGenericTempMeasurement =
		chartConfig.name === 'measurement' && chartConfig.title === 'temperature';
	if (chartConfig.name === 'temperature' || isPillTempMeasurement || isGenericTempMeasurement) {
		chartData.units = tmp_units_from_settings(
			TEMPERATURE_SENSORS_IN_CELSIUS.includes(device.type) ? device : settings,
		);
	}
	for (let i = 0; i < series.length; ++i) {
		const item = series[i],
			historyData = item?.prevPeriod ? prevPeriodData : data,
			reversed = /(returned|reversed)/.test(item.name),
			history = threeEM
				? /total/.test(item.name)
					? historyData?.[item.index]
					: historyData?.history[item.index]
				: historyData?.history;

		chartData.series[i] = { ...item, data: [] };
		if (!history) continue;
		let consumptionDataset: ConsumptionDataset = {};
		const chartName = threeEM
				? chartData.name == 'voltage'
					? item.name.substring(8)
					: chartConfig.name
				: item.name,
			estimateConsumption = /^(total|consumption)$/.test(item.name) && !item.prevPeriod,
			reversedConsumption = /^(reversed)$/.test(item.name) && !item.prevPeriod;
		history.map((obj: GetStatsResponseHistoryData) => {
			if (missingData && !obj?.missing) missingData = false;
			let value: number = obj?.missing ? 0 : reversed ? obj.reversed : obj[chartName],
				parts = obj.datetime.split(/[-\s:T]/),
				dt = Date.UTC(
					Number(parts[0]),
					Number(parts[1]) - 1,
					Number(parts[2]),
					Number(parts[3]),
					Number(parts[4]),
					0,
					0,
				);
			if (isBleStatisticsDeviceToUTC(device, period)) {
				const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
				const offsetHours = getUTCOffset(timezone).hours;
				dt = addDate(dt, 'h', offsetHours).getTime();
			}
			if (!/consumption|voltage/i.test(chartConfig.name)) {
				if (
					chartData.units === '°F' &&
					(chartConfig.name === 'temperature' ||
						isPillTempMeasurement ||
						isGenericTempMeasurement)
				) {
					value = celsius_to_fahrenheit(value);
				} else if (/^(precipitation)$/.test(chartConfig.name)) {
					const unit = device?.ht_ui?.precipitation_unit || 'mm';
					chartData.units = unit;
					value = convertPrecipitationFromMm(value, unit);
				}
				chartData.series[i].data.push({
					dt,
					value,
					name: item.name,
					bgColor: undefined,
				});
			} else {
				let costPower = null;
				let rawCost = obj?.missing ? 0 : obj?.cost;
				if (dummyNetMetering && !reversed && chartConfig.name != 'voltage') {
					let rawValue = value;
					value = Math.max(value - (obj?.reversed || 0), 0);
					if (rawCost) {
						const costPerUnit = (1000 / rawValue) * rawCost;
						rawCost = (value / 1000) * costPerUnit;
					}
				}
				if (reversed) {
					if (dummyNetMetering && value) {
						value = Math.max((obj?.reversed || 0) - (obj?.consumption || 0), 0);
					}
					value = value ? value * -1 : 0;
				} else if (rawCost) {
					const cost = convertCost(rawCost, currency),
						tariffID = obj.tariff_id || '0',
						tariffName = !device?.shared && getTariffByID(tariffID)?.name,
						unit = 'Wh',
						power = convertToValueAndUnit(value, unit);
					costPower = {
						tariff: {
							id: tariffID,
							name: (tariffID != '0' && tariffName) || `Tariff ${tariffID}`,
							cost: rawCost,
							value,
							currency,
							unit,
						},
						cost,
						value: `${power.value} ${power.unit}`,
						show: estimateConsumption,
					};
				}

				if (!consumptionDataset[dt]) {
					consumptionDataset[dt] = {
						dt,
						name: item.name,
						value: value || 0,
						missing: Boolean(obj?.missing),
						active_net: obj.active_net,
						datetime: obj.datetime,
						reversed,
					};
					if (!reversed && chartConfig.name != 'voltage') {
						consumptionDataset[dt].cost = rawCost || 0;
						consumptionDataset[dt].metaData = [costPower];
					}
				} else if (chartConfig.name == 'voltage') {
					consumptionDataset[dt].value = item.name.startsWith('min')
						? Math.min(consumptionDataset[dt].value, value) || 0
						: Math.max(consumptionDataset[dt].value || 0, value || 0);
				} else {
					consumptionDataset[dt].value += value;
					if (obj?.missing && !consumptionDataset[dt].missing) {
						consumptionDataset[dt].missing = true;
					}
					if (!reversed) {
						consumptionDataset[dt].cost += rawCost || 0;
						consumptionDataset[dt].metaData.push(costPower);
					}
				}
			}
		});
		if (Object.keys(consumptionDataset).length) {
			consumptionDataset = calculateNetMetering(consumptionDataset);
			Object.keys(consumptionDataset)
				.sort()
				.forEach((key: string) => {
					const e = consumptionDataset[key];
					if (estimateConsumption) {
						totalConsumption += e.value;
						totalConsumptionCost += e.cost;
					} else if (reversedConsumption) {
						totalReversedConsumption += e.value;
					}
					chartData.series[i].data.push({
						...e,
						prevValue: item?.prevPeriod && prevPeriodData ? e.dt : null,
						convertCost: convertCost(e.cost, currency),
					});
				});
			chartData.series[i].missingData = missingData;
		}
	}
	if (/consumption/i.test(chartConfig.name)) {
		chartData.totalConsumption = getTotalConsumption(totalConsumption, totalConsumptionCost);
		chartData.totalReturnedConsumption = getTotalReturnedConsumption(totalReversedConsumption);
		chartData.devices = data.devices;
		chartData.totalRoomConsuption = totalConsumption;
		chartData.totalReversedConsumption = totalReversedConsumption;
	}
	chartData.timezone = data.timezone;
	return chartData;
};

export const getTotalConsumption = (
	totalConsumption: number,
	totalConsumptionCost: number,
): { icon: string; value: string }[] => {
	const inPowerTotal = convertToValueAndUnit(totalConsumption, 'Wh');
	let labelListConsum = [];
	if (inPowerTotal.value) {
		labelListConsum = [
			{
				icon: 'fas fa-bolt',
				value: `${inPowerTotal.value} ${inPowerTotal.unit}`,
				iconColor: getSystemStyle(SystemStyles.COLOR_PRIMARY),
			},
		];
		if (totalConsumptionCost) {
			const sumCostFixed = convertCost(totalConsumptionCost, get(user_settings).currency);
			if (sumCostFixed) {
				labelListConsum.push({
					icon: 'fas fa-coins',
					value: `${sumCostFixed}`,
					iconColor: getSystemStyle(SystemStyles.COLOR_CHART_MONEY_VALUE),
				});
			}
		}
	}
	return labelListConsum;
};

export const getTotalReturnedConsumption = (
	totalReturnedConsumption: number,
): { icon: string; value: string }[] => {
	const inPowerTotal = convertToValueAndUnit(Math.abs(totalReturnedConsumption), 'Wh');
	let labelListConsum = [];
	if (inPowerTotal.value) {
		labelListConsum = [
			{
				icon: 'fas fa-house-day',
				value: `${inPowerTotal.value} ${inPowerTotal.unit}`,
				iconColor: getSystemStyle(SystemStyles.COLOR_SUCCESS),
			},
		];
	}
	return labelListConsum;
};

const getDeviceCharts = (
	device: Device,
	url: string,
	types: string[],
	units: { [key: string]: string } = {},
	title: string = null,
	deviceChannel: number | string = null,
): ChartConfigInterface => {
	const charts = [],
		prevPeriodComparison = isPrevPeriodComparisonEnabled() !== false;
	types.forEach((type) => {
		const typeSplit = type.split(':'),
			extid = typeSplit[1]
				? `_${typeSplit[1]}`
				: device.category == 'pv'
					? `_${deviceChannel || 0}`
					: '';
		type = typeSplit[0];
		const chart: ChartConfig = {
			id: `${type}${extid}`,
			name: type,
			extid: extid.substring(1),
			sensorID: typeSplit[2] ? typeSplit[2] : extid.substring(1),
			type: CHART_VARIANTS_LIST[device?.[`${type}${extid}_type`]] || CHART_VARIANTS_LIST[0],
			series: null,
			units: '',
			deviceID: real_id(device.id),
			deviceType: device.type,
			deviceCategory: device.category,
			height: DEFAULT_CHART_HEIGHT,
			title,
			deviceChannel,
		};
		switch (type) {
			case 'distance':
				chart.series = [
					{
						name: 'min',
						style: `min_${type}`,
						visible: true,
					},
					{
						name: 'max',
						style: `max_${type}`,
						visible: true,
					},
					{
						name: 'avg',
						style: `avg_${type}`,
						visible: true,
					},
				];
				chart.units = 'mm';
				break;
			case 'temperature':
				chart.series = [
					{
						name: `min_${type}${extid}`,
						style: 'min_temperature',
						visible: true,
					},
					{
						name: `max_${type}${extid}`,
						style: 'max_temperature',
						visible: true,
					},
				];
				chart.units = '°C';
				break;
			case 'humidity':
			case 'moisture':
				chart.series = [
					{
						name: type,
						style: 'humidity',
						visible: true,
					},
				];
				chart.units = '%';
				break;
			case 'precipitation':
				chart.series = [
					{
						name: type,
						style: 'humidity',
						visible: true,
					},
				];
				chart.units = 'mm';
				break;
			case 'pressure':
				chart.series = [
					{
						name: `min_${type}`,
						style: `min_distance`,
						visible: true,
					},
					{
						name: `max_${type}`,
						style: `max_distance`,
						visible: true,
					},
				];
				chart.units = 'hPa';
				break;
			case 'illuminance':
				chart.series = [
					{
						name: type,
						style: 'prev_period_consumption',
						visible: true,
					},
				];
				chart.units = 'lx';
				break;
			case 'uv':
				chart.series = [
					{
						name: type,
						style: 'max_temperature',
						visible: true,
					},
				];
				chart.units = '';
				break;
			case 'consumption':
				if (isThreeEM(device.type) && device?.mode != 'monophase') {
					chart.series = [
						{
							visible: true,
							name: 'phase_a',
							stack: 'phase_a',
							style: 'phase_a',
							index: 0,
						},
						{
							visible: true,
							name: 'phase_b',
							stack: 'phase_b',
							style: 'phase_b',
							index: 1,
						},
						{
							visible: true,
							name: 'phase_c',
							stack: 'phase_c',
							style: 'phase_c',
							index: 2,
						},
						{
							visible: true,
							name: 'total',
							stack: 'total',
							style: 'total_consumption',
							index: 'sum',
						},
						{
							visible: true,
							name: 'phase_a_returned',
							stack: 'phase_a',
							style: 'phase_a_returned',
							index: 0,
						},
						{
							visible: true,
							name: 'phase_b_returned',
							stack: 'phase_b',
							style: 'phase_b_returned',
							index: 1,
						},
						{
							visible: true,
							name: 'phase_c_returned',
							stack: 'phase_c',
							style: 'phase_c_returned',
							index: 2,
						},
						{
							visible: true,
							name: 'total_returned',
							stack: 'total',
							style: 'total_returned',
							index: 'sum',
						},
					];
				} else if (
					isEM(device.type) ||
					device.category == 'pv' ||
					(is_gen_2(device) && device.category != 'roller')
				) {
					chart.series = [
						{
							visible: true,
							name: type,
							stack: 'consumption',
							style: 'consumption',
						},
						{
							visible: true,
							name: 'reversed',
							stack: 'consumption',
							style: 'reversed',
						},
						{
							visible: prevPeriodComparison,
							prevPeriod: true,
							name: type,
							stack: 'prevPeriod',
							style: 'prev_period_consumption',
						},
						{
							visible: prevPeriodComparison,
							prevPeriod: true,
							name: 'reversed',
							stack: 'prevPeriod',
							style: 'prev_period_reversed',
						},
					];
				} else {
					chart.series = [
						{
							name: type,
							style: 'consumption',
							visible: true,
						},
						{
							visible: prevPeriodComparison,
							style: 'prev_period_consumption',
							prevPeriod: true,
							name: type,
						},
					];
				}
				chart.units = 'Wh';
				break;
			case 'voltage':
				if (isThreeEM(device.type)) {
					chart.series = [
						{
							visible: true,
							name: `phase_a_min_${type}`,
							style: 'phase_a_min_voltage',
							index: 0,
						},
						{
							visible: true,
							name: `phase_a_max_${type}`,
							style: 'phase_a_max_voltage',
							index: 0,
						},
						{
							visible: true,
							name: `phase_b_min_${type}`,
							style: 'phase_b_min_voltage',
							index: 1,
						},
						{
							visible: true,
							name: `phase_b_max_${type}`,
							style: 'phase_b_max_voltage',
							index: 1,
						},
						{
							visible: true,
							name: `phase_c_min_${type}`,
							style: 'phase_c_min_voltage',
							index: 2,
						},
						{
							visible: true,
							name: `phase_c_max_${type}`,
							style: 'phase_c_max_voltage',
							index: 2,
						},
					];
					if (device?.mode == 'monophase') {
						chart.series = [
							{
								name: `min_${type}`,
								style: 'min_voltage',
								visible: true,
							},
							{
								name: `max_${type}`,
								style: 'max_voltage',
								visible: true,
							},
						];
					}
				} else if (isEM(device.type)) {
					chart.series = [
						{
							name: `min_${type}`,
							style: 'min_voltage',
							visible: true,
						},
						{
							name: `max_${type}`,
							style: 'max_voltage',
							visible: true,
						},
					];
				} else {
					chart.series = [
						{
							name: type,
							style: 'voltage',
							visible: true,
						},
					];
				}
				chart.units = 'V';
				break;
			case 'voltmeter':
				chart.series = [
					{
						name: `min_voltage${extid}`,
						style: 'min_voltage',
						visible: true,
					},
					{
						name: `max_voltage${extid}`,
						style: 'max_voltage',
						visible: true,
					},
				];
				chart.units = units?.xunit || 'V';
				break;
			case 'input':
				chart.series = [
					{
						name: `min_percent${extid}`,
						style: 'min_input',
						visible: true,
					},
					{
						name: `max_percent${extid}`,
						style: 'max_input',
						visible: true,
					},
				];
				chart.units = units?.xunit || '%';
				break;
			case 'valve_position':
				chart.series = [
					{
						name: 'position',
						style: 'valve_position',
						visible: true,
					},
				];
				chart.units = '%';
				chart.height = 150;
				break;
			case 'ppm':
				chart.series = [
					{
						name: type,
						style: 'ppm',
						visible: true,
					},
				];
				chart.units = 'ppm';
				break;
			case 'accumulation':
			case 'counter_accumulation':
				chart.series = [
					{
						name: 'value',
						style: 'counter_accumulation',
						visible: true,
					},
				];
				chart.units = units[type] || '';
				break;
			case 'measurement':
			case 'freq_measurement':
				chart.series = [
					{
						name: 'min',
						style: /^°(C|F)$/.test(units[type]) ? 'min_temperature' : 'freq_measurement_min',
						visible: true,
					},
					{
						name: 'max',
						style: /^°(C|F)$/.test(units[type]) ? 'max_temperature' : 'freq_measurement_max',
						visible: true,
					},
					{
						name: 'avg',
						style: 'freq_measurement_avg',
						visible: true,
					},
				];
				chart.units = units[type] || '';
				break;
			case 'counter':
				chart.series = [
					{
						name: 'counter',
						style: 'counter_accumulation',
						visible: true,
					},
					{
						name: 'change',
						style: 'freq_measurement_avg',
						visible: true,
					},
				];
				chart.units = units[type] || '';
				break;
		}
		charts.push(chart);
	});
	return { url, charts };
};

export const getDeviceChartsConfig = (device: Device): ChartConfigInterface[] => {
	let charts: ChartConfigInterface[] = [],
		deviceChart: ChartConfigInterface;
	switch (device.category) {
		case 'light':
		case 'roller':
		case 'relay':
			if (devices_config[device.type]?.isPM) {
				deviceChart = getDeviceCharts(
					device,
					'v2/statistics/power-consumption',
					//Show voltage chart for all G2 PM devices
					(is_gen_2(device) || device.type == 'SHSW-25') && device.category != 'roller'
						? ['consumption', 'voltage']
						: ['consumption'],
				);
			}
			if (isWallDisplay(device.type)) {
				//wall display
				deviceChart = getDeviceCharts(device, 'statistics/sensor/values', [
					'temperature',
					'humidity',
				]);
			}
			break;
		case 'sensor':
			switch (device.type) {
				case 'SHHT-1':
				case 'SNSN-0013A':
				case 'S3SN-0U12A': //h&t g3
				case 'S3SN-1U12A': //h&t g3 ffs
				case 'SHSEN-1':
				case 'SBHT-003C':
				case 'SBHT-103C':
				case 'SBHT-203C':
					deviceChart = getDeviceCharts(device, 'statistics/sensor/values', [
						'temperature',
						'humidity',
					]);
					break;
				case 'SHWT-1':
				case 'SHSM-01':
				case 'SHDW-2':
				case 'SHMOS-02':
					deviceChart = getDeviceCharts(device, 'statistics/sensor/values', ['temperature']);
					break;
				case 'SHGS-1':
					deviceChart = getDeviceCharts(device, 'statistics/sensor/concentration', ['ppm']);
					break;
				case 'SBDI-003E':
					deviceChart = getDeviceCharts(
						device,
						'v2/statistics/generic/measurement',
						['distance'],
						{},
						null,
						1,
					);
					break;
				case 'SBWS-90CM': //Weather Station
					deviceChart = getDeviceCharts(device, 'v2/statistics/weather-station', [
						'temperature',
						'humidity',
						'pressure',
						'precipitation',
						'illuminance',
						'uv',
					]);
					break;
				case 'SBMS-001A': //Blu Soil Sensor
					deviceChart = {
						url: 'v2/statistics/generic/measurement',
						charts: [
							...getDeviceCharts(
								device,
								'v2/statistics/generic/measurement',
								['measurement:moisture:0'],
								{ measurement: '%' },
								'moisture',
								'moisture:0',
							).charts,
							...getDeviceCharts(
								device,
								'v2/statistics/generic/measurement',
								['measurement:temperature:0'],
								{ measurement: '°C' },
								'temperature',
								'temperature:0',
							).charts,
						],
					};
					break;
			}
			break;
		case 'thermostat':
			if (isWallDisplay(device.type)) {
				//Wall display has new ability to extract thermostat component from the device
				deviceChart = getDeviceCharts(
					device,
					'statistics/sensor/values',
					['temperature', 'humidity'],
					{},
					null,
					device.channel,
				);
			} else if (!isXT1Module(device.type)) {
				deviceChart = getDeviceCharts(
					device,
					'statistics/sensor/values',
					['temperature', 'valve_position'],
					{},
					null,
					device.channel,
				);
			}
			break;
		case 'emeter':
			const splitPhases = isEM(device.type) || device.mode === 'monophase';
			// console.log('splitPhases', splitPhases)
			deviceChart = getDeviceCharts(
				device,
				`v2/statistics/power-consumption/em-${splitPhases ? '1p' : '3p'}`,
				['consumption', 'voltage'],
			);
			break;
		case 'power_meter':
			deviceChart = getDeviceCharts(device, 'v2/statistics/power-consumption', [
				'consumption',
				'voltage',
			]);
			break;
	}
	if (!deviceChart && devices_config[device.type]?.isPM && isFanDevice(device.type)) {
		deviceChart = getDeviceCharts(
			device,
			'statistics/sensor/values',
			['humidity', 'temperature'],
			{},
			null,
			0,
		);
	}
	if (deviceChart) charts.push(deviceChart);
	// Ventora (S4PB-0916176901): internal env history via statistics/sensor/values on channel 0.
	// Other temperature/humidity charts: sensor category H&T types, thermostat/wall display branches, relay ext_temphum_sensors add-ons (see switch above).
	if (isFanDevice(device.type)) {
		charts.push(getDeviceCharts(device, 'v2/statistics/power-consumption', ['consumption']));
	}
	const addonPeripherals = is_gen_2(device)
		? isPeripheralDevice(device.type)
			? getPeripheralDeviceStatus(device)
			: getAddonStatus(device, [], false, true)
		: getExtSensorsStatus(device);
	const isPillDevice =
		addonPeripherals.length && (isPill(device.type) || isPillPeripheralDevice(device));
	if (isPillDevice) {
		addonPeripherals.forEach((i) => {
			if (!/^(digital)/.test(i.peripheralInputType) && !/^(lora|switch)$/.test(i.peripheralType)) {
				charts.push(
					getDeviceCharts(
						device,
						'v2/statistics/generic/measurement',
						[`measurement:${i.sensor.extid}`],
						{
							measurement:
								i.sensor.xunit ||
								`${i.sensor.type == 'temperature' ? '°' : ''}${i.sensor.unit}`,
						},
						i.sensor.type,
						i.sensor.component,
					),
				);
			}
		});
		return charts;
	}
	if (addonPeripherals.length) {
		const isDHT = addonPeripherals.find((i) => i.sensor.type == 'humidity');
		if (isDHT) {
			charts.push(
				getDeviceCharts(
					device,
					`statistics/relay/ext_temphum_sensors`,
					addonPeripherals
						.filter((i) => /^(temperature|humidity)$/.test(i.sensor.type))
						.map((i) => `${i.sensor.type}::${i.sensor.extid}`),
				),
			);
		} else if (addonPeripherals.find((i) => i.sensor.type == 'temperature')) {
			charts.push(
				getDeviceCharts(
					device,
					'statistics/relay/ext_temp_sensors',
					addonPeripherals
						.filter((i) => i.sensor.type == 'temperature')
						.map((i) => `${i.sensor.type}:${i.sensor.extid}`),
				),
			);
		}
		if (is_gen_2(device)) {
			const peripherals = addonPeripherals.filter(
				(i) => !/^(temperature|humidity)$/.test(i.sensor.type),
			);
			peripherals.forEach((i) => {
				if (
					!/^(digital)$/.test(i.peripheralInputType) &&
					!/^(lora|switch)$/.test(i.peripheralType)
				) {
					const url = `statistics/relay/ext_${
						i.sensor.type == 'voltmeter' ? 'voltage' : i.sensor.type
					}_sensors`;
					charts.push(
						getDeviceCharts(device, url, [`${i.sensor.type}:${i.sensor.extid}`], {
							xunit: i.sensor.xunit,
						}),
					);
				}
			});
		}
	}
	if (isPlusUni(device.type)) {
		const settings = devices_settings.getByRealId(device.id)?.['input:2'];
		if (settings && settings.enable !== false) {
			const xunit = settings?.xcounts?.unit || 'p';
			const freqXUnit = settings?.xfreq?.unit || 'Hz';
			charts.push(
				getDeviceCharts(
					device,
					'v2/statistics/generic/accumulation',
					['counter_accumulation'],
					{ counter_accumulation: xunit },
					null,
					2,
				),
			);
			charts.push(
				getDeviceCharts(
					device,
					'v2/statistics/generic/measurement',
					['freq_measurement'],
					{ freq_measurement: freqXUnit },
					null,
					2,
				),
			);
		}
	} else if (isXT1Module(device.type) || isVirtualDevice(device.type)) {
		const id = device.id;
		const status = devices_status.getByRealId(id);
		const settings = devices_settings.getByRealId(id);
		const deviceStatistics = getVirtualStatistics(status);
		if (settings && deviceStatistics?.length) {
			const component = getComponentByDevice(device);
			const bthomeDevice = device.type == 'bthome-device';
			deviceStatistics.forEach((i: { name: string; value: string; type: string }) => {
				let vcSettings = settings[i.value];
				if (device.type == 'virtual-group') {
					if (!(status?.[component]?.value || []).includes(i.value)) {
						vcSettings = null;
					}
				} else if (bthomeDevice) {
					if (settings?.[component]?.addr != settings?.[i.value]?.addr) {
						vcSettings = null;
					}
				}
				if (vcSettings?.id > 0) {
					const objSetting = bthomeDevice
						? bthomeObjectInfos.find((i) => i.id == vcSettings?.obj_id)
						: vcSettings;
					const channelID = bthomeDevice
						? vcSettings.id + 1000
						: vcSettings.id + (/^object:2\d{2}$/.test(i.value) ? 3000 : 0);
					const unit = bthomeDevice ? objSetting?.unit : vcSettings.meta?.ui?.unit;
					charts.push(
						getDeviceCharts(
							device,
							`v2/statistics/generic/${i.type}`,
							[`${i.type}:${channelID}`],
							{ [`${i.type}`]: unit || '' },
							objSetting?.name || i.value,
							channelID,
						),
					);
				}
			});
		}
	} else if (device.category == 'pv') {
		const url = 'v2/statistics/power-consumption';
		charts.push(getDeviceCharts(device, url, ['consumption'], {}, 'Home', 0));
		charts.push(getDeviceCharts(device, url, ['consumption'], {}, 'Battery', 1));
		charts.push(getDeviceCharts(device, url, ['consumption'], {}, 'Solar Panel', 2));
	}
	return charts;
};

export const getOverAllChartsConfig = (type: ChartVariantsType): ChartConfigInterface => {
	return {
		url: 'v2/statistics/power-consumption/overall',
		charts: [
			{
				name: 'overall_consumption',
				type,
				series: [
					{
						visible: true,
						name: 'consumption',
						style: 'overall_consumption',
						stack: 'overall',
					},
					{
						visible: true,
						name: 'reversed',
						style: 'overall_reversed',
						stack: 'overall',
					},
				],
				units: 'Wh',
				height: DEFAULT_CHART_HEIGHT,
			},
		],
	};
};

export const getTempSchemeChartConfig = (
	range: number,
	tempSchedule: IChartTempSchedule,
	startWeekOnSun = false,
	TRVScheme = false,
): ChartDataInterface => {
	const startDate = Date.UTC(2023, 0, 1, 0, 0, 0, 0),
		data = [],
		sunday = [],
		addDataset = (dt: number, value: number, i: number) => {
			const dateset = { dt, value, day: i };
			i == 0 ? sunday.push(dateset) : data.push(dateset);
		};
	let intervals = [];
	for (let i = 0; i < tempSchedule.intervals.length; ++i) {
		const interval = tempSchedule.intervals[i],
			splitTime = interval.time[0].split(':'),
			days = interval.days;
		for (let j = 0; j < days.length; ++j) {
			let day = days[j];
			if (TRVScheme) {
				day += 1;
				if (day == 7) day = 0;
			}
			const hour = Number(splitTime[0]),
				min = Number(splitTime[1]);
			let time = addDate(startDate, 'h', day * 24 + hour);
			time = addDate(time, 'i', min);
			intervals.push({
				time: time.getTime(),
				day,
				hour,
				min,
				value: interval?.disabled ? null : interval.base,
			});
			if (!TRVScheme) {
				const splitTimeТо = interval.time[1].split(':'),
					hourTo = Number(splitTimeТо[0]),
					minTo = Number(splitTimeТо[1]);
				let dayTo = day,
					timeTo = addDate(startDate, 'h', day * 24 + hourTo);
				timeTo = addDate(timeTo, 'i', minTo);
				if (timeTo.getTime() <= time.getTime()) {
					timeTo = addDate(timeTo, 'd', 1);
					dayTo = timeTo.getDay();
					if (dayTo == 0) {
						timeTo = addDate(timeTo, 'w', -1);
					}
				}
				intervals.push({
					time: timeTo.getTime(),
					day: dayTo,
					hour: hourTo,
					min: minTo,
					base: true,
					value: tempSchedule.base,
				});
			}
		}
	}
	intervals = sortOn(intervals, 'time');
	if (!TRVScheme) {
		const baseIntervals = intervals.filter((v) => v.base);
		for (let i = 0; i < baseIntervals.length; ++i) {
			if (intervals.find((v) => !v.base && v.time == baseIntervals[i].time)) {
				intervals = intervals.filter((v) => v != baseIntervals[i]);
			}
		}
	}
	let value = intervals.length > 0 ? intervals[intervals.length - 1].value : tempSchedule.base;
	for (let i = 0; i < 7; ++i) {
		const date = i > 0 ? addDate(startDate, 'd', i) : startDate;
		for (let j = 0; j < 24; ++j) {
			let dt = addDate(date, 'h', j).getTime();
			const interval = intervals.filter((v) => v.day == i && v.hour == j);
			if (interval.length) {
				if (!interval.find((v) => v.min == 0)) {
					addDataset(dt, value, i);
				}
				for (let int = 0; int < interval.length; ++int) {
					if (value != null && interval[int].value == null) {
						addDataset(addDate(dt, 'i', interval[int].min).getTime(), value, i);
					}
					value = interval[int].value;
					if (interval[int].min != 0) {
						addDataset(addDate(dt, 'i', interval[int].min).getTime(), value, i);
					} else {
						addDataset(dt, value, i);
					}
				}
			} else {
				addDataset(dt, value, i);
			}
		}
	}
	let seriesData = startWeekOnSun ? [...sunday, ...data] : [...data, ...sunday];
	if (range >= 0) seriesData = seriesData.filter((i) => i.day == range);
	return {
		name: 'tempSchedule',
		type: CHART_VARIANTS_LIST[3],
		units: '°' + (tempSchedule.units || 'C'),
		rangeValue: range == -1 ? 'week' : 'day',
		series: [
			{
				name: 'tempSchedule',
				data: seriesData,
				visible: true,
				style: `tempSchedule_${tempSchedule.type}`,
			},
		],
		height: TEMP_SCHEME_CHART_HEIGHT,
		disabled: !TRVScheme && !tempSchedule._enabled,
	};
};

export const getTRVSchemeChartConfig = (
	range: number,
	tempSchedule: IChartTempSchedule,
	startWeekOnSun = false,
): ChartDataInterface => {
	return getTempSchemeChartConfig(range, tempSchedule, startWeekOnSun, true);
};

export const getDynamicChartConfig = (chartDataSet: {
	labels: string[];
	currentValues: number[];
	excludedValues: number[];
	xGrid?: boolean;
}): ChartDataInterface => {
	return {
		name: 'dynamicChart',
		type: 'bar',
		units: 'W',
		labels: chartDataSet.labels,
		loaded: chartDataSet.labels.length > 0,
		series: [
			{
				name: 'current',
				visible: true,
				//@ts-ignore
				data: chartDataSet.currentValues,
				style: 'dynamicChart_current',
			},
			{
				name: 'excluded',
				visible: true,
				//@ts-ignore
				data: chartDataSet.excludedValues,
				style: 'dynamicChart_excluded',
			},
		],
		height: DEFAULT_CHART_HEIGHT,
		xGrid: chartDataSet.xGrid,
		logarithmic: true,
	};
};

export const getDynamicPieChartConfig = (chartDataSet: {
	labels: string[];
	currentValues: any[];
	colors: any[];
}): ChartDataInterface => {
	return {
		name: 'dynamicChart',
		type: 'pie',
		units: 'W',
		labels: chartDataSet.labels,
		loaded: chartDataSet.labels.length > 0,
		series: [
			{
				name: 'dynamicChart',
				visible: true,
				data: chartDataSet.currentValues,
				colors: chartDataSet.colors,
				style: 'none',
			},
		],
		height: DEFAULT_CHART_HEIGHT,
	};
};

export const getTariffIntervalsChartConfig = (
	powerPrices: PowerPricesData,
	units: string,
	range: number,
	startWeekOnSun = false,
): ChartDataInterface => {
	const data = [],
		schedule = powerPrices.schedule,
		startDate = Date.UTC(2023, 0, 1, 0, 0, 0, 0),
		sunday = [],
		series = [];
	for (let i = 0; i < schedule.length; ++i) {
		if (range >= 0 && i != range) continue;
		const date = i > 0 ? addDate(startDate, 'd', i) : startDate;
		for (let j = 0; j < schedule[i].length; ++j) {
			const tariffID = schedule[i][j],
				tariff = powerPrices.tariffs[tariffID],
				dateset = {
					dt: addDate(date, 'i', j * 30).getTime(),
					value: tariff?.price || 0,
					name: tariff?.name || '',
					bgColor: tariff?.bgColor
						? tariff?.bgColor
						: !tariff?.bgColor && tariff?.price > 0
							? 'color-green'
							: undefined,
				};
			i == 0 ? sunday.push(dateset) : data.push(dateset);
		}
	}
	series.push({
		name: 'timeZone',
		data: startWeekOnSun ? [...sunday, ...data] : [...data, ...sunday],
		visible: true,
		style: 'tariff_intervals',
	});
	return {
		name: 'tariffIntervals',
		type: CHART_VARIANTS_LIST[3],
		units,
		rangeValue: range == -1 ? 'week' : 'day',
		series,
		height: DEFAULT_CHART_HEIGHT,
	};
};

export const averageSensorStatistic = (
	chartData: ChartConfig,
	threeEmChannel: number = null,
): ChartStatisticInterface => {
	if (
		threeEmChannel === null &&
		chartData.name == 'voltage' &&
		isThreeEM(chartData.deviceType) &&
		chartData.deviceMode != 'monophase'
	) {
		const result: ChartStatisticInterface = {
			available: true,
		};
		const indexes = [];
		for (let i = 0; i < chartData.series.length; ++i) {
			const phaseData = chartData.series[i];
			let item = indexes[phaseData.index];
			if (!item) {
				item = { ...chartData, phaseName: phaseData.name.substring(0, 7) };
				indexes[phaseData.index] = item;
				item.series = [];
			}
			item.series.push(phaseData);
		}
		result.threeEMStatistic = [];
		for (let i = 0; i < indexes.length; ++i) {
			const phase = averageSensorStatistic(indexes[i], i);
			result.threeEMStatistic.push({
				...phase,
				phaseName: indexes[i].phaseName,
			});
		}
		return result;
	} else if (
		/(temperature|input|voltmeter|measurement|distance|pressure)$/.test(chartData.name) ||
		(chartData.deviceCategory == 'emeter' && chartData.name == 'voltage')
	) {
		let attr = chartData.name;
		if (attr == 'input') {
			attr = 'percent_' + chartData.extid;
		} else if (attr == 'voltmeter') {
			attr = 'voltage_' + chartData.extid;
		}
		let minAttr = `min_${attr}`;
		let maxAttr = `max_${attr}`;
		if (/(measurement|distance)/.test(chartData.name)) {
			minAttr = 'min';
			maxAttr = 'max';
		}
		const dataMinIndex = chartData.series.findIndex((i) => i.name.includes(minAttr)),
			dataMaxIndex = chartData.series.findIndex((i) => i.name.includes(maxAttr));
		let minValues: any[] = chartData.series[dataMinIndex].data,
			maxValues: any[] = chartData.series[dataMaxIndex].data;
		if (chartData.name == 'voltage') {
			minValues = minValues.filter((v) => !v?.missing && v.value != 0);
			maxValues = maxValues.filter((v) => !v?.missing && v.value != 0);
		}
		minValues = minValues.filter((v) => !checkForStatisticError(v.value)).map((v) => v.value);
		maxValues = maxValues.filter((v) => !checkForStatisticError(v.value)).map((v) => v.value);
		const averageValue = average([...minValues, ...maxValues]);
		if (!isNaN(averageValue) && averageValue != 0) {
			let min = Math.min(...minValues),
				max = Math.max(...maxValues);
			const convertToUnitParams = {
				fixed: 1,
				dontConvert: skipUnitConversion(chartData.title),
			};
			return {
				available: true,
				statistic: true,
				average: convertToUnit(averageValue, chartData.units, convertToUnitParams),
				min: convertToUnit(min == Infinity ? 0 : min, chartData.units, convertToUnitParams),
				max: convertToUnit(max == Infinity ? 0 : max, chartData.units, convertToUnitParams),
			};
		}
	} else if (
		isWeatherStation(chartData.deviceType) ||
		/(humidity|valve_position|accumulation|ppm|voltage|counter)$/.test(chartData.name)
	) {
		let attr = chartData.name;
		if (attr == 'valve_position') {
			attr = 'position';
		} else if (/(accumulation)/.test(attr)) {
			attr = 'value';
		}
		const dataIndex = chartData.series.findIndex((i) => i.name == attr);
		let values: any[] = chartData.series[dataIndex]?.data || [];
		values = values.filter((v) => !checkForStatisticError(v.value)).map((v) => v.value);
		const averageValue = average([...values]);
		let totalCounter: string = null;
		const convertToUnitParams = {
			fixed: 1,
			dontConvert: skipUnitConversion(chartData.name),
		};
		if (/(accumulation|precipitation)$/.test(chartData.name)) {
			totalCounter = convertToUnit(sum(values), chartData.units, convertToUnitParams);
		}
		if (!isNaN(averageValue) && averageValue != 0) {
			return {
				available: true,
				statistic: true,
				totalCounter,
				average: convertToUnit(averageValue, chartData.units, convertToUnitParams),
				min: convertToUnit(Math.min(...values), chartData.units, convertToUnitParams),
				max: convertToUnit(Math.max(...values), chartData.units, convertToUnitParams),
			};
		}
	}
	return null;
};

export const estimateChartConsumption = (
	chartData: ChartConfig,
	deviceType: string,
	currency: string,
	threeEmData: { channel: string; prevChartData: ChartConfig } = null,
): ChartStatisticInterface => {
	const dataTotalIndex = threeEmData
			? chartData.series.findIndex((i) => i.name == threeEmData.channel)
			: 0,
		data = chartData.series[dataTotalIndex].data,
		valuesFilter = data.filter((v) => v.metaData !== null && !v?.missing),
		values = valuesFilter.map((v) => v.value),
		total = sum(values),
		costData = data.filter((v) => v.cost !== null && !v?.missing),
		historyIndex = isEM(deviceType) ? 2 : threeEmData ? dataTotalIndex : 1;

	const dataReturnedIndex =
			threeEmData && chartData.deviceMode !== 'monophase'
				? chartData.series.findIndex((i) => i.name == threeEmData.channel + '_returned')
				: chartData.series.findIndex((i) => i.name == 'reversed'),
		dataReturned = dataReturnedIndex >= 0 ? chartData.series[dataReturnedIndex].data : [],
		valuesFilterReturned = dataReturned.filter((v) => v.metaData !== null && !v?.missing),
		valuesReturned = valuesFilterReturned.map((v) => v.value),
		totalReturned = Math.abs(sum(valuesReturned));

	const result: ChartStatisticInterface = {
		name: chartData.name,
		available: total > 0 || totalReturned > 0,
		totalConsumed: convertToUnit(total, chartData.units),
		totalConsumedValue: total,
		totalGenerated: totalReturned > 0 ? convertToUnit(totalReturned, chartData.units) : undefined,
		totalGeneratedValue: totalReturned,
		average: convertToUnit(average(values), chartData.units),
		min: convertToUnit(Math.min(...values), chartData.units),
		max: convertToUnit(Math.max(...values), chartData.units),
		prevTotalConsumedValue: null,
		comparison: null,
		comparisonDiff: null,
		totalCost: null,
		averageCost: null,
		minCost: null,
		maxCost: null,
		threeEMStatistic: null,
		phaseName: null,
		tariffs: null,
		returnedAverage: null,
		returnedMin: null,
		returnedMax: null,
	};

	if (costData && costData.length) {
		const cost = costData.map((v) => Number(v.cost));
		result.totalCost = convertCost(sum(cost), currency);
		result.averageCost = convertCost(average(cost), currency);
		const min = valuesFilter.find((v) => v.value == Math.min(...values)),
			max = valuesFilter.find((v) => v.value == Math.max(...values));
		if (max && max.convertCost) {
			result.maxCost = max.convertCost;
			result.minCost = `0 ${currency || ''}`;
			if (min && min.convertCost) {
				result.minCost = min.convertCost;
			}
		}
		const metaData = costData.map((v) => v.metaData);
		if (metaData?.length) {
			const tariffs = {},
				tariffData = metaData.flat();
			for (let i = 0; i < tariffData.length; ++i) {
				const tariff = tariffData[i]?.tariff;
				if (!tariff) continue;
				if (!tariffs[tariff.id]) {
					tariffs[tariff.id] = tariff;
				} else {
					tariffs[tariff.id].value += tariff.value;
					tariffs[tariff.id].cost += tariff.cost;
				}
			}
			result.tariffs = [];
			for (const tariffID in tariffs) {
				const tariff = tariffs[tariffID];
				result.tariffs.push({
					name: tariff.name,
					consumed: convertToUnit(tariff.value, tariff.unit),
					cost: convertCost(tariff.cost, tariff.currency),
				});
			}
		}
	}

	const prevValues = ((threeEmData?.prevChartData || chartData)?.series?.[historyIndex]?.data || [])
			.slice(0, values.length)
			.map((v) => v.value),
		prevTotal = sum(prevValues);
	if (prevTotal > 0) {
		result.prevTotal = prevTotal;
		result.comparison = total <= prevTotal;
		result.comparisonDiff = mathEpsilon(Math.abs(100 - (total / prevTotal) * 100), 1);
	}

	if (threeEmData?.channel == 'total') {
		result.phaseName = 'total';
		result.threeEMStatistic = [];
		for (let i = 0; i < chartData.series.length; ++i) {
			const phaseData = chartData.series[i];
			if (!/total/.test(phaseData.name)) {
				const phase = estimateChartConsumption(chartData, deviceType, currency, {
					...threeEmData,
					channel: phaseData.name,
				});
				result.threeEMStatistic.push({ ...phase, phaseName: phaseData.name });
			}
		}
	}

	if (totalReturned > 0) {
		result.returnedAverage = convertToUnit(Math.abs(average(valuesReturned)), chartData.units);
		result.returnedMin = convertToUnit(Math.abs(Math.min(...valuesReturned)), chartData.units);
		result.returnedMax = convertToUnit(Math.abs(Math.max(...valuesReturned)), chartData.units);
	}

	return result;
};

export const addLastHourConsumption = (
	resData: GetStatsResponseData,
	lastHourRes: GetStatsResponseData,
	chartControlsData: chartControlsDataInterface,
	threeEM: boolean = false,
): GetStatsResponseData => {
	if (!lastHourRes?.history?.length) return resData;
	resData = deepCloneObj(resData);
	const lastHourResHistory: GetStatsResponseHistoryData[] = lastHourRes.history,
		firstItem = threeEM ? lastHourResHistory[0][0] : lastHourResHistory[0],
		date = new Date(firstItem.datetime);
	date.setMinutes(0);
	if (!/^24_hours|day$/.test(chartControlsData.rangeOption.period)) {
		date.setHours(0);
	}
	if (
		/^12_months|year$/.test(chartControlsData.rangeOption.period) ||
		(chartControlsData.rangeOption.period == 'custom' && resData?.interval == 'month')
	) {
		date.setDate(1);
	}
	const dateStr = dateFormat(date, chartControlsData.format);
	if (threeEM) {
		for (let i = 0; i < resData.history.length; i++) {
			//@ts-ignore
			resData.history[i] = addLastHourConsumptionByChannel(
				//@ts-ignore
				resData.history[i],
				lastHourResHistory[i],
				dateStr,
			);
		}
		resData.sum = addLastHourConsumptionByChannel(resData.sum, lastHourRes.sum, dateStr);
	} else {
		resData.history = addLastHourConsumptionByChannel(resData.history, lastHourResHistory, dateStr);
	}
	if (lastHourRes?.devices) {
		const items = lastHourRes.devices;
		for (const dev in items) {
			if (resData.devices[dev]) {
				resData.devices[dev].consumption += items[dev].consumption || 0;
				resData.devices[dev].reversed += items[dev].reversed || 0;
				resData.devices[dev].cost += items[dev].cost || 0;
			} else {
				resData.devices[dev] = items[dev];
			}
		}
	}
	return resData;
};

const addLastHourConsumptionByChannel = (
	resHistory: GetStatsResponseHistoryData[],
	lastHourResHistory: GetStatsResponseHistoryData[],
	date: string,
) => {
	const resHistoryCopy = resHistory.filter((i) => i.datetime != date);
	let items = addLastHourConsumptionDateset(lastHourResHistory, date);
	items = addLastHourConsumptionDateset(
		resHistory.filter((i) => i.datetime == date),
		date,
		items,
	);
	if (Object.keys(items).length > 0) {
		for (const i in items) {
			resHistoryCopy.push(items[i]);
		}
	}
	return sortOn(resHistoryCopy, 'datetime');
};

const addLastHourConsumptionDateset = (
	resHistory: GetStatsResponseHistoryData[],
	date: string,
	items = {},
) => {
	for (let i = 0; i < resHistory.length; i++) {
		const historyItem: GetStatsResponseHistoryData = resHistory[i];
		if (!historyItem?.missing) {
			const tariff_id = historyItem.tariff_id || '0';
			if (!items[tariff_id]) {
				items[tariff_id] = { ...historyItem, datetime: date };
			} else {
				items[tariff_id].consumption += historyItem.consumption;
				items[tariff_id].reversed += historyItem.reversed;
				items[tariff_id].cost += historyItem.cost;
				items[tariff_id].min_voltage =
					Math.min(items[tariff_id].min_voltage, historyItem.min_voltage) || 0;
				items[tariff_id].max_voltage = Math.max(
					items[tariff_id].max_voltage || 0,
					historyItem.max_voltage || 0,
				);
				items[tariff_id].voltage = Math.max(
					items[tariff_id].voltage || 0,
					historyItem.voltage || 0,
				);
			}
		}
	}
	return items;
};

export const getMonthlyReport = (
	type: 'hour' | 'day' | 'week',
	dataSet: MonthlyReport,
	year: number,
	month: number,
	currency: string,
): ChartDataInterface => {
	const startDate = Date.UTC(type == 'week' ? 2023 : year, type == 'week' ? 0 : month, 1, 0, 0, 0, 0),
		consumptionData = [],
		periods = {
			hour: 24,
			week: 7,
			day: new Date(year, month + 1, 0).getDate(),
		},
		days = getNumberOfWeekDaysInMonth(month, year);
	let maxValue = 0,
		mostConsumingData = null;
	for (let i = 0; i < periods[type]; ++i) {
		let dt = addDate(startDate, type == 'week' ? 'd' : type, i),
			key = type == 'day' ? i + 1 : i,
			value =
				typeof dataSet?.[key] !== 'undefined' || dataSet?.[key] != null
					? dataSet[key].consumption
					: 0,
			cost = dataSet?.[key]?.cost,
			reversed =
				(typeof dataSet?.[key] !== 'undefined' || dataSet?.[key] != null) &&
				dataSet[key]?.reversed
					? -Math.abs(dataSet[key].reversed)
					: 0;
		if (type == 'week') {
			const day = dt.getDay();
			value /= days[day];
			cost /= days[day];
			reversed /= days[day];
		} else if (type == 'hour') {
			value /= periods.day;
			cost /= periods.day;
			reversed /= periods.day;
		}
		maxValue = Math.max(maxValue, value);
		if (maxValue == value || i == 0) {
			mostConsumingData = {
				index: key,
				consumption: value,
				cost,
				reversed,
			};
		}
		consumptionData.push({
			dt: dt.getTime(),
			value,
			reversed,
			convertCost: cost ? convertCost(cost, currency) : null,
		});
	}
	if (type == 'week' && !startsWeekOnSun()) {
		consumptionData.push(consumptionData.shift());
	}

	return {
		name: 'monthlyReport',
		type: type == 'week' ? 'areaspline' : 'bar',
		units: 'Wh',
		rangeValue: type == 'day' ? 'month' : type == 'week' ? 'day_of_week' : type,
		series: [
			{
				visible: true,
				name: 'consumption',
				data: consumptionData,
				style: 'monthly_report_consumption',
				stack: 'monthlyReport',
			},
			{
				visible: true,
				name: 'reversed',
				data: consumptionData.map((i) => (i = { dt: i.dt, value: i.reversed })),
				style: 'monthly_report_reversed',
				stack: 'monthlyReport',
			},
		],
		//@ts-ignore
		mostConsumingData,
		height: DEFAULT_CHART_HEIGHT,
	};
};

const getVirtualStatistics = (status) => {
	const deviceMeasurements = (status?._measurements || []).map((i) => {
		return { ...i, type: 'measurement' };
	});
	const deviceAccumulations = (status?._accumulations || []).map((i) => {
		return { ...i, type: 'accumulation' };
	});
	const deviceCounters = (status?._counters || []).map((i) => {
		return { ...i, type: 'counter' };
	});
	return [...deviceMeasurements, ...deviceAccumulations, ...deviceCounters];
};

/**
 * Adds the last hour's measurement statistics to the current statistics
 * if the current time period includes the last hour.
 *
 * @param {GetStatsResponseData} resData - The original response data that will be updated.
 * @param {GetStatsResponseData} lastHourRes - The response data containing the last hour's measurements.
 * @param {chartControlsDataInterface} chartControlsData - Configuration object controlling the chart's display settings.
 * @param {boolean} [isGBLE=false] - Indicates whether the device is of type GBLE.
 * GBLE devices do not have timezone settings and always use UTC time.
 * @returns {GetStatsResponseData} The updated response data with the last hour's measurements integrated.
 */

export const addLastHourMeasurement = (
	resData: GetStatsResponseData,
	lastHourRes: GetStatsResponseData,
	chartControlsData: chartControlsDataInterface,
	isGBLE = false,
): GetStatsResponseData => {
	if (!lastHourRes?.history?.length) return resData;
	resData = deepCloneObj(resData);
	const lastHourResHistory: GetStatsResponseHistoryData[] = lastHourRes.history,
		firstItem = lastHourResHistory[0],
		date = new Date(firstItem.datetime);
	date.setMinutes(0);
	if (!/^(24_hours|day)$/.test(chartControlsData.rangeOption.period)) {
		date.setHours(0);
	}
	if (
		/^(12_months|year)$/.test(chartControlsData.rangeOption.period) ||
		(chartControlsData.rangeOption.period == 'custom' && resData?.interval == 'month')
	) {
		date.setDate(1);
	}
	resData.history = addLastHourMeasurementDateset(
		resData?.history || [],
		lastHourResHistory,
		date,
		isGBLE,
	);
	return resData;
};

export const addLastHourMeasurementDateset = (
	resHistory: GetStatsResponseHistoryData[],
	lastHourResHistory: GetStatsResponseHistoryData[],
	date: Date,
	isGBLE = false,
): GetStatsResponseHistoryData[] => {
	const measurementFormat = 'Y-m-dTH:i:s';
	const datetime: string = dateFormat(date, measurementFormat, isGBLE);
	const resHistoryCopy: GetStatsResponseHistoryData[] = resHistory.filter(
		(i) => !i?.missing && dateFormat(i.datetime, measurementFormat, isGBLE) != datetime,
	);
	const avg: number[] = [];
	const propAvg: { prop?: string; values?: number[] } = {};
	const item: GetStatsResponseHistoryData = resHistory.find(
		(i) => dateFormat(i.datetime, measurementFormat, isGBLE) == datetime,
	) || { datetime, missing: true };
	for (let i = 0; i < lastHourResHistory.length; i++) {
		const historyItem: GetStatsResponseHistoryData = lastHourResHistory[i];
		if (historyItem?.missing) continue;
		for (const prop in historyItem) {
			if (prop == 'datetime') continue;
			item.missing = false;
			if (/^(min|max)$/.test(prop)) {
				item[prop] = Math[prop](item[prop] ?? historyItem[prop], historyItem[prop]);
			} else if (prop == 'avg') {
				avg.push(historyItem.avg || 0);
			} else if (/^(value|precipitation)$/.test(prop)) {
				item[prop] = (item[prop] || 0) + (historyItem[prop] || 0);
			} else {
				if (!propAvg[prop]) propAvg[prop] = [];
				propAvg[prop].push(historyItem[prop] || 0);
			}
		}
	}
	if (avg.length) {
		if (item.avg != null) avg.push(item.avg || 0);
		item.avg = average(avg);
	} else {
		for (const prop in propAvg) {
			if (item[prop] != null) propAvg[prop].push(item[prop] || 0);
			item[prop] = average(propAvg[prop]);
		}
	}
	resHistoryCopy.push(item);
	return sortOn(resHistoryCopy, 'datetime');
};
